<template>
    <div class="w-100">
        <SectionTitle title="Significant Turn Summary">
            <MoreInfo :id="'turn-info-modal'" :title="'Significant Turn Summary Description'">
                <SignificantTurnSummaryInfoText :includeEntrySpeed="true"
                    :turnEntrySpeedBoundaries="turnEntrySpeedBoundaries" />
            </MoreInfo>
        </SectionTitle>
        <MissingDataText v-if="!hasData" :message="'Significant Turn Summary Unavailable'" />
        <div v-else>
            <div class="p-0">
                <RadioButton :id="'turn-side-radio-group'" :modal="selectedTurnSide" :options="turnSideOptions"
                    :name="'turn-options'" @update-modal="selectedTurnSide = $event" :task="buildTurnRatesData" />
                <RadioButton :id="'turn-radio-group'" :modal="selectedTurnClass" :options="turnClassOptions" :label="''"
                    :name="'season-radio-options'" @update-modal="selectedTurnClass = $event"
                    :task="buildTurnRatesData" />
                <MissingDataText message="No results match your current filters" v-if="turnRatesData.turns.length === 0"/>
                <div class="turn-rates-chart-container" v-else>
                    <TurnRatesPlot width="100%" :viewport="viewport" id="turn-rates-chart" :data="turnRatesData"
                        title="" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { UserData } from "@/components/UserData";
import {
    getTurnRatesPercentages,
    getTurnRatesRanges,
    getTurnRatesLabels,
    getSeasonTurnData,
    getSessionTurns,
    extractTurnInfo,
    getFilterOptions,
    getClassificationNames,
    getFilteredTurnCounts,
    determineTurnType,
} from "@/utils/TurnRateHelpers";
import Colours from "@/utils/Colours";
import { Season, getSeasons } from "@/utils/Seasons";
import turnFiltersStore from "./turnFiltersStore";


export default {
    props: {
        data: Object,
    },
    components: {
    },
    data() {
        return {
            angleData: [],
            accelerationsData: [],
            decelerationsData: [],
            entrySpeedData: [],
            turnEntrySpeedBoundaries: UserData.getTurnEntrySpeedBoundaries(),
            selectedTurnClass: 'byTotalTurns',
            turnClassOptions: [
                { text: 'Total Turns', value: 'byTotalTurns' },
                { text: 'Entry Speed', value: 'byEntrySpeed' },
                { text: 'Turn Angle', value: 'byAngleClass' },
                { text: 'Turn Peak Accelerations', value: 'byAccelerationPeakClass' },
                { text: 'Turn Peak Decelerations', value: 'byDecelerationPeakClass' },
            ],
            selectedTurnSide: 'leftRight',
            turnSideOptions: [
                { text: 'Left & Right', value: 'leftRight' },
                { text: 'Combined', value: 'combined' }
            ],
            turnRatesData: null,
            viewport: null,
            seasonSelected: Season.ALL,
            hasData: false
        };
    },
    methods: {
        buildTurnRatesData() {
            this.hasData = this.data.sessionTurnClassCounts.length > 0;
            if (this.hasData) {
                const { sessionTurnClassCounts, turnsZScores } = this.data;

                const earliestDate = new Date(Math.min(...sessionTurnClassCounts.map(session => new Date(session.sessionDate).valueOf())));
                this.seasonOptions = getSeasons(earliestDate, true);

                const seasonData = getSeasonTurnData(sessionTurnClassCounts, this.filterModel.seasonSelected);
                const sessionTurns = getSessionTurns(
                    this.filterModel.longitudinalView, seasonData, sessionTurnClassCounts,
                    this.filterModel.range
                );
                const { turns: rawTurns, sessionDates, tooltipTitles } = extractTurnInfo(sessionTurns, this.selectedTurnClass);
                const filterOptions = getFilterOptions(this.selectedTurnClass, this.filterModel);
                const classificationNames = getClassificationNames(this.selectedTurnClass, this.turnEntrySpeedBoundaries);
                const filteredTurns = getFilteredTurnCounts(
                    rawTurns,
                    filterOptions,
                    classificationNames,
                    this.filterModel.longitudinalView
                );
                const type = determineTurnType(this.selectedTurnClass, this.selectedTurnSide);

                const turnRatesPercentages = getTurnRatesPercentages(filteredTurns);
                const turnRatesRanges = getTurnRatesRanges(filteredTurns, [turnsZScores], [false]);
                const turnRatesLabels = getTurnRatesLabels(sessionDates, turnRatesRanges.leftTurnNormalRange, true, false);
                const sharedData = { ...turnRatesRanges, ...turnRatesPercentages, labels: turnRatesLabels, ...type, tooltipTitles };

                this.turnRatesData = {
                    turns: filteredTurns,
                    classificationNames,
                    colours: Colours.TURN_RATES[this.selectedTurnClass],
                    tooltipTitles,
                    ...sharedData,
                };
                if (sessionDates.length === 1) {
                    this.viewport = 24;
                } else if (sessionDates.length >= 2 & sessionDates.length <6) {
                    this.viewport = sessionDates.length * 14;
                } else {
                    this.viewport = sessionDates.length * 8;
                }
            }
        },
    },
    computed: {
        filters() {
            return turnFiltersStore.state;
        },
        filterModel() {
            return turnFiltersStore.state;
        },
        updateKey() {
            return turnFiltersStore.state.updateKey;
        }
    },
    watch: {
        updateKey: {
            handler() {
                this.buildTurnRatesData();
            },
            immediate: true
        }, 
        data: {
            handler() {
                this.buildTurnRatesData();
            },
            deep: true
        }, 
    },
    async mounted() {
        this.buildTurnRatesData();
    },
};
</script>

<style>
.turn-rates-chart-container {
    max-height: 55vh;
    overflow-y: auto;
    scrollbar-width: none;
}
</style>